import { useState } from 'react';
import { AxiosError } from 'axios';
import { IErrorReturnService, IGenericError } from '../models/errors';
import { getAPIErrorData } from '../utils/api-helpers';

// Alternate error response structure from some APIs
interface AlternateErrorResponse {
  errorData?: {
    errors?: {
      error?: IErrorReturnService[];
    };
  };
}

export interface FetchFnResponse<T> {
  data: Awaited<T> | null;
  error: IErrorReturnService | Error | null;
}

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: IErrorReturnService | Error | null;
  errorMsg: string | null;
  isFetched: boolean;
  fetchData: (fetchFunction: () => Promise<T>) => Promise<FetchFnResponse<T>>;
}

const useFetchState = <T>(): FetchState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<IErrorReturnService | Error | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fetchData = async (fetchFunction: () => Promise<T>) => {
    let result: Awaited<T> | null = null;
    setLoading(true);
    try {
      result = await fetchFunction();
      setData(result);
      setError(null);
      setErrorMsg(null);
      return { data: result, error: null };
    } catch (err) {
      setData(null);
      // Check (loosely) if it's an AxiosError
      if ('response' in (err as AxiosError)) {
        const axiosError = err as AxiosError<IGenericError>;
        const errorData = getAPIErrorData(axiosError);
        if (!errorData) {
          let tempError: IErrorReturnService | Error | undefined = (
            axiosError as AxiosError<AlternateErrorResponse>
          )?.response?.data?.errorData?.errors?.error?.[0];
          if (!tempError) {
            tempError = new Error('No error data found');
          }
          setError(tempError);
          setErrorMsg('No error data found');
          return { data: null, error: tempError };
        }
        setError(errorData);
        setErrorMsg(errorData?.description);
        return { data: null, error: errorData };
      }
      if (err instanceof Error) {
        setError(err);
        setErrorMsg(err.message);
        return { data: null, error: err };
      }
      return { data: null, error: err as Error };
    } finally {
      setLoading(false);
      setIsFetched(true);
    }
  };

  return {
    data,
    loading,
    error,
    errorMsg,
    isFetched,
    fetchData,
  };
};

export default useFetchState;
